import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { fmtMoney } from './Leads';
import StatusPill from '../../components/StatusPill';

type Stats = { byStatus: { status: string; n: number; spend: string }[]; totalLeads: number; pipelineSpend: string; totalPropostas: number };

const ROLE_CTA: Record<string, { to: string; label: string; desc: string }[]> = {
  admin: [
    { to: '/painel/importar', label: 'Importar batch', desc: 'trazer linhas da Claro para o CRM' },
    { to: '/painel/prospeccao', label: 'Prospecção', desc: 'buscar empresas e pontuar' },
    { to: '/painel/usuarios', label: 'Time', desc: 'gestores e vendedores' },
  ],
  gestor: [
    { to: '/painel/importar', label: 'Importar batch', desc: 'trazer linhas da Claro para o CRM' },
    { to: '/painel/ofertas', label: 'Ofertas do mês', desc: 'atualizar o que vai ofertar' },
    { to: '/painel/distribuicao', label: 'Distribuir', desc: 'mandar leads aos vendedores' },
    { to: '/painel/prospeccao', label: 'Prospecção', desc: 'buscar empresas e pontuar' },
  ],
  vendedor: [
    { to: '/painel/leads', label: 'Meus leads', desc: 'quem você atende agora' },
    { to: '/painel/propostas', label: 'Propostas', desc: 'rascunhos e envios' },
  ],
};

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  useEffect(() => {
    api.get<Stats>('/stats').then(setStats).catch(() => setStats(null));
  }, []);

  const byStatus = Object.fromEntries((stats?.byStatus ?? []).map((s) => [s.status, s]));
  const kpis = [
    { label: 'Leads no funil', value: (stats?.totalLeads ?? 0).toLocaleString('pt-BR'), sub: `${byStatus['novo']?.n ?? 0} ainda sem contato` },
    { label: 'Pipeline (gasto/mês)', value: fmtMoney(stats?.pipelineSpend ?? '0'), sub: 'soma das contas atuais' },
    { label: 'Propostas', value: (stats?.totalPropostas ?? 0).toString(), sub: `${byStatus['proposta_enviada']?.n ?? 0} enviadas` },
    { label: 'Ganhos', value: (byStatus['fechado_ganho']?.n ?? 0).toString(), sub: `${byStatus['fechado_perdido']?.n ?? 0} perdidos` },
  ];
  const { user } = useAuth();
  const ctas = ROLE_CTA[user?.role ?? 'vendedor'] ?? ROLE_CTA.vendedor;

  return (
    <div>
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Início</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">{user?.fullName}</h1>
      </header>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <section key={k.label} className="rounded-lg border border-[var(--border)] bg-white p-4">
            <p className="text-xs font-medium text-[var(--text-dim)]">{k.label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight">{k.value}</p>
            <p className="mt-1 text-xs text-[var(--text-dim)]">{k.sub}</p>
          </section>
        ))}
      </div>
      <section className="mt-6">
        <h2 className="text-sm font-semibold">O que fazer agora</h2>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {ctas.map((c) => (
            <Link key={c.to} to={c.to} className="rounded-lg border border-[var(--border)] bg-white p-4 transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-subtle)]">
              <span className="block text-sm font-medium">{c.label}</span>
              <span className="mt-0.5 block text-xs text-[var(--text-dim)]">{c.desc}</span>
            </Link>
          ))}
        </div>
      </section>
      <section className="mt-6">
        <h2 className="text-sm font-semibold">Funil por status</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(byStatus).filter(([, s]) => s.n > 0).map(([status, s]) => (
            <Link key={status} to={`/painel/leads?status=${status}`} className="flex items-center gap-2 rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm hover:bg-[var(--bg-subtle)]">
              <StatusPill status={status} />
              <span className="font-semibold tabular-nums">{s.n}</span>
              <span className="text-xs text-[var(--text-dim)]">{fmtMoney(s.spend)}</span>
            </Link>
          ))}
          {(stats?.totalLeads ?? 0) === 0 && (
            <p className="text-sm text-[var(--text-dim)]">Sem leads ainda. Comece importando um batch da Claro.</p>
          )}
        </div>
      </section>
    </div>
  );
}
